import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import {
  Badge,
  Empty,
  Field,
  FormGrid,
  Modal,
  Notice,
  Page,
  Panel,
  StatTile,
  StateBadge,
  Table,
  TileRow,
  fmtDateTime,
  fmtDuration,
  usePoll,
} from '../components/ui'
import type { AgentPerf, LiveBoard, MonitorMode, Role, UserRow } from '../types'

export default function Agents({ role, userId }: { role: Role; userId: string }) {
  const live = usePoll<LiveBoard>(() => api.liveAgents(), 3000)
  const [users, setUsers] = useState<UserRow[]>([])
  const [perf, setPerf] = useState<AgentPerf[]>([])
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<UserRow | null>(null)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [refresh, setRefresh] = useState(0)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.users().then(setUsers).catch(() => setUsers([]))
    api.agentPerformance().then(setPerf).catch(() => setPerf([]))
  }, [refresh])

  const board = live.data
  const perfOf = (id: string) => perf.find((p) => p.id === id)

  // The supervisor's own live session, taken from the board rather than held
  // separately. The board is the dialer's own in-memory state, so if it does
  // not list you, you genuinely have no line and cannot listen to anything —
  // which is exactly what the buttons need to reflect.
  const mySession = board?.agents.find((a) => a.user_id === userId)?.session_id ?? null
  const watching = board?.agents.find((a) => a.session_id === mySession)?.monitoring ?? null

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true)
    try {
      await fn()
      // Refresh immediately rather than waiting up to three seconds for the
      // poll. A supervisor who presses Listen and sees nothing change presses
      // it again.
      live.reload()
      setNotice({ kind: 'success', text: ok })
    } catch (e) {
      setNotice({
        kind: 'error',
        text: e instanceof ApiError ? e.message : String(e),
      })
    } finally {
      setBusy(false)
    }
  }

  const startMonitor = (target: string, mode: MonitorMode) => {
    if (!mySession) return
    const said = {
      listen: 'Listening. Neither party has been told.',
      whisper: 'Whispering. The agent can hear you; the customer cannot.',
      barge: 'Barged in. Everybody can hear you.',
    }[mode]
    return act(() => api.monitorStart(mySession, target, mode), said)
  }

  const stopMonitor = () => {
    if (!mySession) return
    return act(() => api.monitorStop(mySession), 'Detached.')
  }

  const endCall = (session: string, who: string) => {
    // Irreversible and audible to the customer, so it asks. The other three
    // controls are reversible and do not.
    if (!confirm(`End ${who}'s call now? The customer will be disconnected.`)) return
    return act(() => api.forceHangup(session), 'Call ended.')
  }


  return (
    <Page
      title="Agents"
      subtitle="Who is online now, and who exists"
      actions={
        role === 'admin' && (
          <button className="primary" onClick={() => setCreating(true)}>
            Add person
          </button>
        )
      }
    >
      {notice && (
        <Notice kind={notice.kind} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <TileRow>
        <StatTile label="Online" value={board?.summary.online ?? 0} tone="live" />
        <StatTile label="Ready" value={board?.summary.ready ?? 0} tone="ok" />
        <StatTile label="Talking" value={board?.summary.talking ?? 0} />
        <StatTile label="Wrap" value={board?.summary.wrap ?? 0} />
        <StatTile label="Paused" value={board?.summary.paused ?? 0} />
        <StatTile label="Dialing" value={board?.summary.dialing ?? 0} />
      </TileRow>

      <Panel
        title="On the floor"
        actions={
          <span className="muted small">
            {mySession
              ? 'refreshes every 3s'
              : 'go online in the Dialer to listen in'}
          </span>
        }
      >
        {!board || board.agents.length === 0 ? (
          <Empty
            message="Nobody is online."
            hint="Agents appear here the moment they go online in the Dialer."
          />
        ) : (
          <Table
            head={[
              'Agent',
              'Campaign',
              'State',
              'Calls today',
              'Talk time',
              'Occupancy',
              'Supervise',
            ]}
          >
            {board.agents.map((a) => {
              const p = perfOf(a.user_id)
              const isMe = a.session_id === mySession
              const onCall = a.state === 'talking'
              // Attached to *this* agent, by me.
              const myAttachment =
                watching && watching.target_session === a.session_id ? watching.mode : null

              return (
                <tr key={a.session_id} className={myAttachment ? 'row-selected' : undefined}>
                  <td>
                    <span className="strong">{a.full_name}</span>
                    <div className="muted small">{a.username}</div>
                    {/* Shown on the agent's own row so a supervisor can see at a
                        glance that somebody else is already on this call. */}
                    {a.monitored_by.length > 0 && (
                      <div className="small">
                        {a.monitored_by.map((m, i) => (
                          <Badge key={i} tone={m.mode === 'listen' ? 'muted' : 'warn'}>
                            {m.supervisor} · {m.mode}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>{a.campaign ?? <span className="muted">—</span>}</td>
                  <td><StateBadge state={a.state} /></td>
                  <td>{p?.calls ?? 0}</td>
                  <td className="mono">{fmtDuration(p?.total_talk_sec ?? 0)}</td>
                  <td>{p ? `${p.occupancy_pct}%` : '—'}</td>
                  <td className="row-actions">
                    {isMe ? (
                      <span className="muted small">you</span>
                    ) : myAttachment ? (
                      <button className="chip on" onClick={stopMonitor} disabled={busy}>
                        Stop {myAttachment}
                      </button>
                    ) : !mySession ? (
                      <span className="muted small">go online to listen</span>
                    ) : !onCall ? (
                      <span className="muted small">not on a call</span>
                    ) : (
                      <>
                        <button
                          className="chip"
                          disabled={busy}
                          onClick={() => startMonitor(a.session_id, 'listen')}
                          title="Hear both sides. Neither party is told."
                        >
                          Listen
                        </button>
                        <button
                          className="chip"
                          disabled={busy}
                          onClick={() => startMonitor(a.session_id, 'whisper')}
                          title="Coach the agent. The customer cannot hear you."
                        >
                          Whisper
                        </button>
                        <button
                          className="chip"
                          disabled={busy}
                          onClick={() => startMonitor(a.session_id, 'barge')}
                          title="Join the call. Everybody hears you."
                        >
                          Barge
                        </button>
                        <button
                          className="link danger"
                          disabled={busy}
                          onClick={() => endCall(a.session_id, a.full_name)}
                          title="End this call now."
                        >
                          End call
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              )
            })}
          </Table>
        )}
      </Panel>

      <Panel title="People">
        {users.length === 0 ? (
          <Empty message="No accounts yet." />
        ) : (
          <Table head={['Name', 'Username', 'Role', 'SIP endpoint', 'Last sign-in', 'Status', '']}>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="strong">{u.full_name}</td>
                <td className="mono">{u.username}</td>
                <td><Badge tone={u.role === 'admin' ? 'warn' : 'muted'}>{u.role}</Badge></td>
                <td className="mono muted">{u.sip_username ?? '—'}</td>
                <td className="muted">{fmtDateTime(u.last_login_at)}</td>
                <td>
                  <Badge tone={u.active ? 'ok' : 'muted'}>{u.active ? 'active' : 'disabled'}</Badge>
                </td>
                <td className="row-actions">
                  {role === 'admin' && (
                    <button className="link" onClick={() => setEditing(u)}>Edit</button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel title="Performance today">
        {perf.length === 0 ? (
          <Empty message="No agent activity today." />
        ) : (
          <Table head={['Agent', 'Calls', 'Contacts', 'Sales', 'Contact rate', 'Avg talk', 'Total talk', 'Paused', 'Occupancy']}>
            {perf.map((p) => (
              <tr key={p.id}>
                <td className="strong">{p.full_name}</td>
                <td>{p.calls}</td>
                <td>{p.contacts}</td>
                <td>{p.sales}</td>
                <td>{p.contact_rate}%</td>
                <td className="mono">{fmtDuration(p.avg_talk_sec)}</td>
                <td className="mono">{fmtDuration(p.total_talk_sec)}</td>
                <td className="mono muted">{fmtDuration(p.pause_sec)}</td>
                <td>{p.occupancy_pct}%</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {creating && (
        <PersonEditor
          onClose={() => setCreating(false)}
          onSaved={(msg) => {
            setCreating(false)
            setRefresh((r) => r + 1)
            setNotice({ kind: 'success', text: msg })
          }}
        />
      )}

      {editing && (
        <PersonEditor
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null)
            setRefresh((r) => r + 1)
            setNotice({ kind: 'success', text: msg })
          }}
        />
      )}
    </Page>
  )
}

// ========================================================= person editor ====

function PersonEditor({
  user,
  onClose,
  onSaved,
}: {
  user?: UserRow
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [username, setUsername] = useState(user?.username ?? '')
  const [fullName, setFullName] = useState(user?.full_name ?? '')
  const [role, setRole] = useState<Role>(user?.role ?? 'agent')
  const [password, setPassword] = useState('')
  const [active, setActive] = useState(user?.active ?? true)
  const [provisionSip, setProvisionSip] = useState(!user)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [provisioned, setProvisioned] = useState<{ sip_username?: string; pjsip_config?: string } | null>(null)

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      if (user) {
        await api.updateUser(user.id, {
          full_name: fullName,
          role,
          active,
          password: password || undefined,
        })
        onSaved(`${fullName} updated.`)
      } else {
        const created = await api.createUser({
          username,
          full_name: fullName,
          role,
          password,
          provision_sip: provisionSip,
        })
        if (created.pjsip_config) {
          // The SIP secret is shown once. It has to go into pjsip_agents.conf on
          // the media server, and after this dialog it only exists in the
          // database.
          setProvisioned(created)
        } else {
          onSaved(`${fullName} created.`)
        }
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  if (provisioned) {
    return (
      <Modal title="SIP endpoint provisioned" onClose={() => onSaved(`${fullName} created.`)} wide>
        <Notice kind="warn">
          This is the only time the SIP password is shown. Add the block below to
          <code> /etc/asterisk/pjsip_agents.conf</code> on the media server and reload PJSIP, or the
          agent cannot register a phone.
        </Notice>
        <p className="muted small">Endpoint: <b className="mono">{provisioned.sip_username}</b></p>
        <pre className="code-block">{provisioned.pjsip_config}</pre>
        <div className="inline-form">
          <button
            className="ghost"
            onClick={() => navigator.clipboard?.writeText(provisioned.pjsip_config ?? '')}
          >
            Copy config
          </button>
          <code className="muted small">sudo asterisk -rx &quot;pjsip reload&quot;</code>
        </div>
        <div className="modal-actions">
          <button className="primary" onClick={() => onSaved(`${fullName} created.`)}>Done</button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title={user ? `Edit ${user.full_name}` : 'Add person'} onClose={onClose}>
      {error && <Notice kind="error">{error}</Notice>}

      <FormGrid>
        <Field label="Full name">
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
        </Field>
        <Field label="Username">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={!!user}
          />
        </Field>
        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="agent">Agent — takes calls</option>
            <option value="supervisor">Supervisor — sees everything, no settings</option>
            <option value="admin">Admin — full control</option>
          </select>
        </Field>
        <Field
          label={user ? 'New password' : 'Password'}
          hint={user ? 'Leave blank to keep the current one.' : 'At least 8 characters.'}
        >
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </FormGrid>

      <div className="check-row">
        {!user && role === 'agent' && (
          <label>
            <input
              type="checkbox"
              checked={provisionSip}
              onChange={(e) => setProvisionSip(e.target.checked)}
            />
            Provision a SIP endpoint (required to take calls)
          </label>
        )}
        {user && (
          <label>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        )}
      </div>

      <div className="modal-actions">
        <button className="ghost" onClick={onClose}>Cancel</button>
        <button
          className="primary"
          onClick={save}
          disabled={busy || !fullName.trim() || (!user && (!username.trim() || password.length < 8))}
        >
          {busy ? 'Saving…' : user ? 'Save' : 'Create'}
        </button>
      </div>
    </Modal>
  )
}
